import React, { useState } from 'react';
import { Mail, Loader2, Check, Lock, Sparkles } from 'lucide-react';
import { read as readAttribution } from '../utils/utm';
import { trackPlausibleEvent } from '../utils/tracking';

const UNLOCK_STORAGE_KEY = 'bbk_prompty_unlocked';

export function isPromptyUnlocked(): boolean {
    if (typeof window === 'undefined') return false;
    return localStorage.getItem(UNLOCK_STORAGE_KEY) === '1';
}

export function setPromptyUnlocked() {
    if (typeof window === 'undefined') return;
    try { localStorage.setItem(UNLOCK_STORAGE_KEY, '1'); } catch {}
}

interface PromptPaywallProps {
    promptSlug?: string;
    promptTitle?: string;
    onUnlocked: () => void;
    onClose?: () => void;
    variant?: 'modal' | 'inline';
}

export const PromptPaywall: React.FC<PromptPaywallProps> = ({ promptSlug, promptTitle, onUnlocked, onClose, variant = 'modal' }) => {
    const [email, setEmail] = useState('');
    const [status, setStatus] = useState<'idle' | 'submitting' | 'ok' | 'error'>('idle');
    const [error, setError] = useState<string | null>(null);

    async function handleSubmit(e: React.FormEvent) {
        e.preventDefault();
        if (status === 'submitting' || !email) return;
        setStatus('submitting');
        setError(null);
        try {
            const attribution = readAttribution() || {};
            const res = await fetch('/api/prompty/unlock', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email, prompt_slug: promptSlug, attribution }),
            });
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            setPromptyUnlocked();
            trackPlausibleEvent('prompty_unlock', { prompt_slug: promptSlug || 'listing' });
            (window as any).dataLayer = (window as any).dataLayer || [];
            (window as any).dataLayer.push({
                event: 'newsletter_signup',
                form_source: 'prompty_gate',
                prompt_slug: promptSlug,
            });
            setStatus('ok');
            setTimeout(() => onUnlocked(), 400);
        } catch (err) {
            console.error('[PromptPaywall] failed', err);
            setError('Coś poszło nie tak. Spróbuj jeszcze raz.');
            setStatus('error');
        }
    }

    const body = (
        <div className="text-center">
            <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-amber-100 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400 mb-4">
                <Lock className="w-7 h-7" />
            </div>
            <h3 className="text-2xl font-bold mb-2">
                {promptTitle ? `Odblokuj: ${promptTitle}` : 'Odblokuj całą bibliotekę promptów'}
            </h3>
            <p className="text-gray-600 dark:text-gray-400 mb-2 max-w-md mx-auto">
                Wpisz email — dostaniesz pełny prompt + dostęp do <b>całej biblioteki</b> i codziennego newslettera z najlepszymi promptami AI pod e-commerce i MŚP.
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-500 mb-6">
                <Sparkles className="inline w-3 h-3 mr-1" /> 50+ promptów ChatGPT, Claude, Midjourney. Bez spamu, jedna wiadomość dziennie.
            </p>

            {status === 'ok' ? (
                <div className="flex items-center justify-center gap-2 text-emerald-600 dark:text-emerald-400 font-semibold py-4">
                    <Check className="w-5 h-5" /> Odblokowane! Wczytuję prompt…
                </div>
            ) : (
                <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto">
                    <div className="relative flex-grow">
                        <Mail className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
                        <input
                            type="email"
                            name="email"
                            value={email}
                            onChange={e => setEmail(e.target.value)}
                            placeholder="Twój email"
                            required
                            disabled={status === 'submitting'}
                            className="w-full pl-12 pr-4 py-3 border border-gray-300 dark:border-gray-700 rounded-full bg-white dark:bg-gray-800 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:opacity-50"
                        />
                    </div>
                    <button
                        type="submit"
                        disabled={status === 'submitting'}
                        className="px-6 py-3 font-semibold text-white bg-gradient-to-r from-blue-600 to-violet-600 rounded-full hover:opacity-90 transition-opacity disabled:opacity-50 inline-flex items-center justify-center gap-2 whitespace-nowrap"
                    >
                        {status === 'submitting' ? <><Loader2 className="w-4 h-4 animate-spin" /> Odblokowuję…</> : 'Odblokuj'}
                    </button>
                </form>
            )}

            {error && <p className="text-sm text-red-600 mt-3">{error}</p>}

            <p className="text-xs text-gray-400 dark:text-gray-600 mt-4">
                Zapisując się akceptujesz <a href="/polityka-prywatnosci" className="underline">politykę prywatności</a>. Możesz się wypisać w każdej chwili.
            </p>
        </div>
    );

    if (variant === 'inline') {
        return (
            <div className="bg-gradient-to-br from-blue-50 to-violet-50 dark:from-gray-900 dark:to-gray-800 border border-blue-100 dark:border-gray-700 rounded-2xl p-8">
                {body}
            </div>
        );
    }

    return (
        <div className="fixed inset-0 z-[10000] bg-black/60 flex items-center justify-center p-4">
            <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl max-w-lg w-full p-8 relative">
                {onClose && (
                    <button
                        onClick={onClose}
                        className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 text-2xl leading-none"
                        aria-label="Zamknij"
                    >×</button>
                )}
                {body}
            </div>
        </div>
    );
};
